import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { SquareSplitHorizontal, Check, X } from "@phosphor-icons/react";
import styled from "@emotion/styled";
import { kfContentIn, twRing } from "@/lib/emotionTw";
import { Button } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import type { TableColumnVisibilityProps } from "./DataTable.types";

/* Exact compiled output of the pre-migration Tailwind utilities. `&&`
 * overrides reproduce the old tailwind-merge behaviour against Button. */

const ColumnVisibilityTrigger = styled(Button, {
  label: "AgenticTableColumnVisibility-trigger"
})({
  '&&[data-state="open"]': {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
  }
});

const ColumnVisibilityContent = styled(DropdownMenu.Content, {
  label: "AgenticTableColumnVisibility-content"
})({
  zIndex: 1000,
  display: "flex",
  width: "14rem",
  flexDirection: "column",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-shadow": "var(--bfrs-shadow-elevated)",
  "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  ...kfContentIn,
  animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)",
  maxHeight: "min(24rem,var(--radix-dropdown-menu-content-available-height))",
  overflow: "auto"
});

const ColumnVisibilityHeader = styled("div", {
  label: "AgenticTableColumnVisibility-header"
})({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: ".5rem",
  paddingBottom: ".5rem"
});

const ColumnVisibilityTitle = styled("span", {
  label: "AgenticTableColumnVisibility-title"
})({
  fontSize: "13px",
  fontWeight: 700,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const ColumnVisibilityClose = styled("button", {
  label: "AgenticTableColumnVisibility-close"
})({
  display: "flex",
  width: "1.25rem",
  height: "1.25rem",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-md)",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
});

const ColumnVisibilityList = styled("div", {
  label: "AgenticTableColumnVisibility-list"
})({
  maxHeight: "18rem",
  overflow: "auto",
  padding: ".25rem"
});

const ColumnVisibilityItem = styled(DropdownMenu.CheckboxItem, {
  label: "AgenticTableColumnVisibility-item"
})({
  display: "flex",
  cursor: "pointer",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  userSelect: "none",
  alignItems: "center",
  gap: ".5rem",
  borderRadius: "var(--bfrs-radius-md)",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: ".375rem",
  paddingBottom: ".375rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&[data-disabled]": {
    cursor: "default",
    pointerEvents: "none",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  "&[data-highlighted]": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
  }
});

const ColumnVisibilityCheckbox = styled("span", {
  label: "AgenticTableColumnVisibility-checkbox",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { checked: boolean; disabled: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "1rem",
    height: "1rem",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: ".25rem",
    borderWidth: "1px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.checked
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
      },
  ownerState.disabled && { opacity: ".5" }
]);

const ColumnVisibilityItemLabel = styled("span", {
  label: "AgenticTableColumnVisibility-itemLabel"
})({
  minWidth: "0px",
  flex: "1 1 0%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const ColumnVisibilityHiddenTag = styled("span", {
  label: "AgenticTableColumnVisibility-hiddenTag"
})({
  fontSize: "10px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const ColumnVisibilityFooter = styled("div", {
  label: "AgenticTableColumnVisibility-footer"
})({
  display: "flex",
  alignItems: "center",
  gap: ".5rem",
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: ".5rem",
  paddingBottom: ".5rem"
});

const ColumnVisibilityFooterButton = styled(Button, {
  label: "AgenticTableColumnVisibility-footerButton"
})({
  "&&": { flex: "1 1 0%" }
});

export function TableColumnVisibility({
  columns,
  visibleColumnIds,
  onVisibleColumnIdsChange,
  label = "Columns",
  resetLabel = "Reset",
  onReset,
  showAllLabel = "Show all",
  onShowAll,
  className
}: TableColumnVisibilityProps) {
  const [open, setOpen] = useState(false);

  const toggle = (id: string) => {
    const next = visibleColumnIds.includes(id)
      ? visibleColumnIds.filter((columnId) => columnId !== id)
      : [...visibleColumnIds, id];
    onVisibleColumnIdsChange(next);
  };

  const showAll = () => {
    if (onShowAll) onShowAll();
    else onVisibleColumnIdsChange(columns.map((column) => column.id));
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <ColumnVisibilityTrigger
          variant="secondary"
          size="sm"
          iconOnly
          aria-label={typeof label === "string" ? label : "Columns"}
          className={className}
        >
          <SquareSplitHorizontal size={16} />
        </ColumnVisibilityTrigger>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <BfrsPortalScope>
          <ColumnVisibilityContent align="end" sideOffset={6} collisionPadding={floatingCollisionPadding}>
            <ColumnVisibilityHeader>
              <ColumnVisibilityTitle>{label}</ColumnVisibilityTitle>
              <ColumnVisibilityClose type="button" aria-label="Close" onClick={() => setOpen(false)}>
                <X size={14} />
              </ColumnVisibilityClose>
            </ColumnVisibilityHeader>
            <ColumnVisibilityList>
              {columns.map((column) => {
                const checked = visibleColumnIds.includes(column.id);
                return (
                  <ColumnVisibilityItem
                    key={column.id}
                    checked={checked}
                    disabled={column.disabled}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={() => toggle(column.id)}
                  >
                    <ColumnVisibilityCheckbox ownerState={{ checked, disabled: Boolean(column.disabled) }}>
                      {checked && <Check size={11} weight="bold" />}
                    </ColumnVisibilityCheckbox>
                    <ColumnVisibilityItemLabel>{column.label}</ColumnVisibilityItemLabel>
                    {!checked && <ColumnVisibilityHiddenTag>hidden</ColumnVisibilityHiddenTag>}
                  </ColumnVisibilityItem>
                );
              })}
            </ColumnVisibilityList>
            <ColumnVisibilityFooter>
              <ColumnVisibilityFooterButton variant="secondary" size="sm" onClick={showAll}>
                {showAllLabel}
              </ColumnVisibilityFooterButton>
              {onReset && (
                <ColumnVisibilityFooterButton variant="primary" size="sm" onClick={onReset}>
                  {resetLabel}
                </ColumnVisibilityFooterButton>
              )}
            </ColumnVisibilityFooter>
          </ColumnVisibilityContent>
        </BfrsPortalScope>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
